"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { OrbitDiagram } from "@/components/tools/physics-fields/orbit-diagram";
import {
  DiagramFrame,
  QuantityField,
  type SigChoice,
  SigFigsField,
  unitOptions,
  WorkingLines,
} from "@/components/tools/physics-fields/shared";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { BODIES, findBody } from "@/lib/tool-kit/physics-fields/bodies";
import { formatSig } from "@/lib/tool-kit/physics-fields/format";
import { readQuantity } from "@/lib/tool-kit/physics-fields/input";
import {
  describeDuration,
  type OrbitError,
  type OrbitKind,
  solveOrbit,
} from "@/lib/tool-kit/physics-fields/orbit";
import { orbitWorking } from "@/lib/tool-kit/physics-fields/orbit-working";
import {
  ACCELERATION_UNITS,
  DISTANCE_UNITS,
  fromBase,
  MASS_UNITS,
  SPEED_UNITS,
  TIME_UNITS,
  type UnitDef,
} from "@/lib/tool-kit/physics-fields/units";

/** English strings. A translated content file overrides these through `ui`. */
export const ORBITAL_VELOCITY_CALCULATOR_STRINGS = {
  exampleLabel: "Start from an example",
  custom: "Custom values",
  exampleNames: {
    iss: "A space station 408 km above Earth",
    geo: "Geostationary orbit (one sidereal day)",
    gps: "A GPS satellite, 20,200 km up",
    moon: "The Moon around Earth",
    "earth-sun": "Earth around the Sun",
    "mars-sun": "Mars around the Sun",
    "lunar-orbit": "A satellite 100 km above the Moon",
  },
  bodyLabel: "Central body",
  bodyNames: {
    sun: "Sun",
    mercury: "Mercury",
    venus: "Venus",
    earth: "Earth",
    moon: "Moon",
    mars: "Mars",
    jupiter: "Jupiter",
    saturn: "Saturn",
    uranus: "Uranus",
    neptune: "Neptune",
    pluto: "Pluto",
  },
  bodyMass: "Mass of the central body M",
  bodyRadius: "Radius of the central body R",
  bodyRadiusHint: "Needed for altitude and surface speeds; optional otherwise.",
  describeBy: "Describe the orbit by",
  kindRadius: "Orbital radius",
  kindAltitude: "Altitude",
  kindSpeed: "Orbital speed",
  kindPeriod: "Orbital period",
  radiusLabel: "Orbital radius r (from the centre)",
  altitudeLabel: "Altitude h above the surface",
  speedLabel: "Orbital speed v",
  periodLabel: "Orbital period T",
  unitFor: "Unit for {name}",
  showSpeedIn: "Show speeds in",
  sigFigs: "Significant figures",
  sigFigsMessage:
    "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  orbitalSpeed: "Orbital speed",
  period: "Orbital period · {duration}",
  periodPlain: "Orbital period",
  escapeSpeed: "Escape speed at this radius",
  orbitalRadius: "Orbital radius",
  altitude: "Altitude",
  acceleration: "Gravitational acceleration",
  surfaceHeading: "At the surface of the central body",
  surfaceCircular: "Circular speed just above the surface",
  surfaceEscape: "Escape speed from the surface",
  keplerLine: "Kepler's third law: T² / r³ = 4π² / (G M) = {constant} s²/m³",
  working: "The working",
  diagramCaption:
    "Schematic, not to scale: gravity points along r and the speed v is perpendicular to it.",
  diagramLabel: "A satellite in a circular orbit",
  diagramDescription:
    "A circle labelled M for the central body, a dashed circular orbit around it, a satellite on the orbit joined to the centre by a line r, and an arrow v tangent to the orbit.",
  errorMissing:
    "Enter the mass of the central body and a value for the orbit. Scientific notation such as 5.97e24 works.",
  errorPositive:
    "The mass, radius, speed and period must be greater than zero, and an altitude cannot be negative.",
  errorRadiusNeeded:
    "An altitude is measured from the surface, so enter the radius of the central body too.",
  errorInsideBody:
    "That orbit would lie inside the central body — its radius is smaller than the body's. Choose a larger radius, altitude or period, or a smaller speed.",
  errorRange:
    "Those numbers are too large or too small to calculate. Check the units.",
  assumptions:
    "Assumes a circular orbit around a spherical body, with the satellite's mass negligible beside it, and no atmosphere, rotation or other bodies. For gas giants the radius is taken at the 1-bar level.",
} as const;

type Strings = typeof ORBITAL_VELOCITY_CALCULATOR_STRINGS;

const MASS_WITH_BODIES: readonly UnitDef[] = [
  ...MASS_UNITS,
  { id: "Me", symbol: "M⊕", factor: 5.9722e24 },
  { id: "Msun", symbol: "M☉", factor: 1.9884e30 },
];

const KIND_UNITS: Record<OrbitKind, readonly UnitDef[]> = {
  radius: DISTANCE_UNITS,
  altitude: DISTANCE_UNITS,
  speed: SPEED_UNITS,
  period: TIME_UNITS,
};

const KINDS: OrbitKind[] = ["radius", "altitude", "speed", "period"];

interface Example {
  body: string;
  kind: OrbitKind;
  value: string;
  unit: string;
}

const EXAMPLES: Record<string, Example> = {
  iss: { body: "earth", kind: "altitude", value: "408", unit: "km" },
  geo: { body: "earth", kind: "period", value: "86164.09", unit: "s" },
  gps: { body: "earth", kind: "altitude", value: "20200", unit: "km" },
  moon: { body: "earth", kind: "radius", value: "384400", unit: "km" },
  "earth-sun": { body: "sun", kind: "radius", value: "149598000", unit: "km" },
  "mars-sun": { body: "sun", kind: "radius", value: "227956000", unit: "km" },
  "lunar-orbit": { body: "moon", kind: "altitude", value: "100", unit: "km" },
};

function sci(value: number): string {
  return value.toExponential().replace("e+", "e");
}

function typed(value: number, sig: number): string {
  return Number(value.toPrecision(sig)).toExponential().replace("e+", "e");
}

function Failure({ error, strings }: { error: OrbitError; strings: Strings }) {
  const messages: Record<OrbitError, string> = {
    missing: strings.errorMissing,
    "non-positive": strings.errorPositive,
    "radius-needed": strings.errorRadiusNeeded,
    "inside-body": strings.errorInsideBody,
    "not-finite": strings.errorRange,
  };
  return error === "missing" ? (
    <Notice>{messages.missing}</Notice>
  ) : (
    <ErrorNotice>{messages[error]}</ErrorNotice>
  );
}

export default function OrbitalVelocityCalculatorWidget() {
  const strings = useToolUi(ORBITAL_VELOCITY_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [sigChoice, setSigChoice] = useState<SigChoice>("4");
  const sig = Number(sigChoice);
  const earth = findBody("earth");
  const [example, setExample] = useState("iss");
  const [body, setBody] = useState("earth");
  const [mass, setMass] = useState({ text: sci(earth?.mass ?? 0), unit: "kg" });
  const [radius, setRadius] = useState({ text: "6371", unit: "km" });
  const [kind, setKind] = useState<OrbitKind>("altitude");
  const [texts, setTexts] = useState<Record<OrbitKind, string>>({
    radius: "",
    altitude: "408",
    speed: "",
    period: "",
  });
  const [units, setUnits] = useState<Record<OrbitKind, string>>({
    radius: "km",
    altitude: "km",
    speed: "km/s",
    period: "min",
  });
  const [speedUnit, setSpeedUnit] = useState("km/s");

  const massSI = readQuantity(mass.text, MASS_WITH_BODIES, mass.unit);
  const radiusSI = readQuantity(radius.text, DISTANCE_UNITS, radius.unit);
  const outcome = solveOrbit({
    mass: massSI,
    radius: radiusSI,
    kind,
    value: readQuantity(texts[kind], KIND_UNITS[kind], units[kind]),
  });
  const result = outcome.ok ? outcome.value : null;

  const pickBody = (id: string) => {
    setBody(id);
    setExample("custom");
    const b = findBody(id);
    if (b) {
      setMass({ text: sci(b.mass), unit: "kg" });
      setRadius({ text: String(b.radius / 1000), unit: "km" });
    }
  };
  const pickExample = (id: string) => {
    setExample(id);
    const ex = EXAMPLES[id];
    const b = ex ? findBody(ex.body) : null;
    if (!(ex && b)) {
      return;
    }
    setBody(ex.body);
    setMass({ text: sci(b.mass), unit: "kg" });
    setRadius({ text: String(b.radius / 1000), unit: "km" });
    setKind(ex.kind);
    setTexts((c) => ({ ...c, [ex.kind]: ex.value }));
    setUnits((c) => ({ ...c, [ex.kind]: ex.unit }));
  };
  const pickKind = (next: OrbitKind) => {
    const known: Record<OrbitKind, number | null> = {
      radius: result?.distance ?? null,
      altitude: result?.altitude ?? null,
      speed: result?.speed ?? null,
      period: result?.period ?? null,
    };
    const carried = known[next];
    if (carried !== null) {
      setTexts((c) => ({
        ...c,
        [next]: typed(fromBase(KIND_UNITS[next], units[next], carried), sig),
      }));
    }
    setKind(next);
  };

  const kindLabels: Record<OrbitKind, string> = {
    radius: strings.radiusLabel,
    altitude: strings.altitudeLabel,
    speed: strings.speedLabel,
    period: strings.periodLabel,
  };
  const bodyNames = strings.bodyNames as Record<string, string>;
  const exampleNames = strings.exampleNames as Record<string, string>;
  const unitLabel = (name: string) => t(strings.unitFor, { name });
  const km = (value: number) => `${formatSig(fmt, value / 1000, sig)} km`;
  const speed = (value: number) =>
    `${formatSig(fmt, fromBase(SPEED_UNITS, speedUnit, value), sig)} ${
      SPEED_UNITS.find((u) => u.id === speedUnit)?.symbol ?? ""
    }`;

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="ov-example"
          label={strings.exampleLabel}
          onChange={pickExample}
          options={[
            { value: "custom", label: strings.custom },
            ...Object.keys(EXAMPLES).map((id) => ({
              value: id,
              label: exampleNames[id] ?? id,
            })),
          ]}
          value={example}
        />
        <SelectField
          id="ov-body"
          label={strings.bodyLabel}
          onChange={pickBody}
          options={[
            { value: "custom", label: strings.custom },
            ...BODIES.map((b) => ({
              value: b.id,
              label: bodyNames[b.id] ?? b.id,
            })),
          ]}
          value={body}
        />
        <QuantityField
          id="ov-mass"
          label={strings.bodyMass}
          onChange={(text) => {
            setBody("custom");
            setMass({ ...mass, text });
          }}
          onUnit={(unit) => setMass({ ...mass, unit })}
          unit={mass.unit}
          unitLabel={unitLabel(strings.bodyMass)}
          units={MASS_WITH_BODIES}
          value={mass.text}
        />
        <QuantityField
          hint={strings.bodyRadiusHint}
          id="ov-radius"
          label={strings.bodyRadius}
          onChange={(text) => {
            setBody("custom");
            setRadius({ ...radius, text });
          }}
          onUnit={(unit) => setRadius({ ...radius, unit })}
          unit={radius.unit}
          unitLabel={unitLabel(strings.bodyRadius)}
          units={DISTANCE_UNITS}
          value={radius.text}
        />
      </div>
      <PillGroup
        label={strings.describeBy}
        name="ov-kind"
        onChange={pickKind}
        options={KINDS.map((k) => ({
          value: k,
          label: {
            radius: strings.kindRadius,
            altitude: strings.kindAltitude,
            speed: strings.kindSpeed,
            period: strings.kindPeriod,
          }[k],
        }))}
        value={kind}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <QuantityField
          id="ov-orbit"
          label={kindLabels[kind]}
          onChange={(text) => {
            setExample("custom");
            setTexts({ ...texts, [kind]: text });
          }}
          onUnit={(unit) => setUnits({ ...units, [kind]: unit })}
          unit={units[kind]}
          unitLabel={unitLabel(kindLabels[kind])}
          units={KIND_UNITS[kind]}
          value={texts[kind]}
        />
        <SelectField
          id="ov-speed-unit"
          label={strings.showSpeedIn}
          onChange={setSpeedUnit}
          options={unitOptions(SPEED_UNITS)}
          value={speedUnit}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {result === null || massSI === null ? (
          <Failure
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.orbitalSpeed}
                value={speed(result.speed)}
              />
              <StatCard
                label={
                  describeDuration(result.period) === ""
                    ? strings.periodPlain
                    : t(strings.period, {
                        duration: describeDuration(result.period),
                      })
                }
                value={`${formatSig(fmt, result.period, sig)} s`}
              />
              <StatCard
                label={strings.escapeSpeed}
                value={speed(result.escapeSpeed)}
              />
              <StatCard
                label={strings.orbitalRadius}
                value={km(result.distance)}
              />
              <StatCard
                label={strings.altitude}
                value={result.altitude === null ? "—" : km(result.altitude)}
              />
              <StatCard
                label={strings.acceleration}
                value={`${formatSig(
                  fmt,
                  fromBase(ACCELERATION_UNITS, "m/s2", result.acceleration),
                  sig
                )} m/s²`}
              />
            </StatGrid>
            {result.surfaceCircularSpeed === null ||
            result.surfaceEscapeSpeed === null ? null : (
              <>
                <h3 className={WIDGET_H3}>{strings.surfaceHeading}</h3>
                <StatGrid>
                  <StatCard
                    label={strings.surfaceCircular}
                    value={speed(result.surfaceCircularSpeed)}
                  />
                  <StatCard
                    label={strings.surfaceEscape}
                    value={speed(result.surfaceEscapeSpeed)}
                  />
                </StatGrid>
              </>
            )}
            <WorkingLines
              heading={strings.working}
              lines={[
                ...orbitWorking(kind, massSI, result, fmt, sig),
                t(strings.keplerLine, {
                  constant: formatSig(fmt, (4 * Math.PI ** 2) / result.mu, sig),
                }),
              ]}
            />
            <DiagramFrame caption={strings.diagramCaption}>
              <OrbitDiagram
                description={strings.diagramDescription}
                id="ov-diagram"
                label={strings.diagramLabel}
                ratio={radiusSI === null ? 1.8 : result.distance / radiusSI}
              />
            </DiagramFrame>
          </>
        )}
      </section>
      <SigFigsField
        format={fmt}
        id="ov-sig"
        label={strings.sigFigs}
        locale={locale}
        message={strings.sigFigsMessage}
        onChange={setSigChoice}
        value={sigChoice}
      />
      <Notice>{strings.assumptions}</Notice>
    </>
  );
}
